/*
 * Authors: mariushirjanu
 * Created: 02/09/2021
 */
import { NgModule } from '@angular/core';
import { Transition, UIRouterModule } from '@uirouter/angular';
import { SharedModule } from '@app/shared/shared.module';
import { CommonModule } from '@angular/common';
import { DashboardUsageComponent } from './usage/dashboard-usage.component';
import { STATE_BROWSELIST, STATE_BROWSELISTUSAGE, STATE_CHART, STATE_CHART_BP, STATE_CHART_CUSTOMER, STATE_DASHBOARD, STATE_DASHBOARDUSAGE, STATE_DESK_OVERVIEW, STATE_REPORT } from '@app/shared/app-states';
import { DashboardCode, DashboardMetadataService } from './dashboard/dashboard-metadata.service';
import { DashboardService } from './dashboard/dashboard.service';
import { ReportComponent } from './report.component';
import { ReportFiltersComponent } from './report-filters.component';
import { StoredReportComponent } from './stored-report/stored-report.component';
import { StoredReportDialogComponent, StoredReportDialogNewButtonComponent } from './stored-report/stored-report-dialog.component';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { util } from '@app/shared/util';
import { ReportFiltersCollapsibleComponent } from './report-filters-collapsible.component';
import { DeskOverviewComponent } from './desk-overview/desk-overview.component';
import { DeskOverviewDataService } from './desk-overview/desk-overview-data.service';
import { ChartComponent } from './chart/chart.component';
import { ChartCustomerComponent } from './chart/chart-customer.component';
import { ChartBpComponent } from './chart/chart-bp.component';
import { ChartService } from './chart/chart.service';
import { OdDataService } from './dashboard/filters/od/od-data.service';
import { DashboardComponent } from './dashboard/dashboard.component';
import { TestDashboardFiltersComponent } from './dashboard/filters/test/test-dashboard-filters.component';
import { BaseDashboardFiltersComponent } from './dashboard/filters/base-dashboard-filters.component';
import { ReportFiltersUtil } from '@app/shared/report/report-filters-util.service';
import { GaStateDeclaration, IObject } from '@type_definitions/datatypes';
import { OdDashboardFiltersComponent } from './dashboard/filters/od/od-dashboard-filters.component';
import { BpLocalEurInfoComponent } from './dashboard/info/bp-local-eur-info.component';
import { CustomerDashboardFiltersComponent } from './dashboard/filters/customer/customer-dashboard-filters.component';
import { NumberOfFansInfoComponent } from './dashboard/info/number-of-fans-info.component';
import { SalesDashboardInfoComponent } from './dashboard/info/sales-dashboard-info.component';
import { CustomerDataService } from './dashboard/filters/customer/customer-data.service';
import { SalesKpiDashboardFiltersComponent } from './dashboard/filters/sales/sales-kpi-dashboard-filters.component';
import { SalesKpiInfoComponent } from './dashboard/info/sales-kpi-info.component';
import { SalesKpiFooterComponent } from './dashboard/footer/sales-kpi-footer.component';
import { LbdmRevenueUsdInfoComponent } from './dashboard/info/lbdm-revenue-usd-info.component';
import { LbdmDashboardFiltersComponent } from './dashboard/filters/lbdm/lbdm-dashboard-filters.component';
import { LbdmDashboardHeaderComponent } from './dashboard/header/lbdm-dashboard-header.component';
import { ActivationKpiInfoComponent } from './dashboard/info/activation-kpi-info.component';
import { ActivationDashboardFiltersComponent } from './dashboard/filters/activation/activation-dashboard-filters.component';
import { ActivationKpiFooterComponent } from './dashboard/footer/activation-kpi-footer.component';
import { OemDashboardFiltersComponent } from './dashboard/filters/oem/oem-dashboard-filters.component';
import { BpSetupDashboardFiltersComponent } from './dashboard/filters/bp-setup/bp-setup-dashboard-filters.component';
import { HitRateDashboardFiltersComponent } from './dashboard/filters/hit-rate/hit-rate-dashboard-filters.component';
import { TranslationCategory } from '@app/shared/services/ga-state.service';
import { Lang } from '@app/shared/language/language-data.service';
import { DashboardSectionComponent } from './dashboard/dashboard-section.component';
import { DashboardSectionsComponent } from './dashboard/dashboard-sections.component';
import { CustomerDashboardSubtitleComponent } from './dashboard/subtitle/customer-dashboard-subtitle.component';
import { OrderDashboardHeaderComponent } from './dashboard/header/order-dashboard-header.component';
import { BrowselistUsageComponent } from './usage/browselist-usage.component';
import { ReportTableSettingsComponent } from './report-table-settings.component';
import { ReportSQLComponent } from './report-sql.component';
import { REPORT_PARAMS_DECLARATION, REPORT_QUERY_PARAMS, REPORT_PARAMS_RESOLVER } from '@app/main/report/report-state.util';
import { TRANSLATION_READY_RESOLVER } from '@app/app-routes.config';
import { FullChartDialogComponent } from '@app/main/report/chart/full-chart-dialog.component';

export const states:GaStateDeclaration[] = [
  {
    name: STATE_REPORT,
    url: `/reports/{reportId}?${REPORT_QUERY_PARAMS}`,
    views: {
      content: {
        component: ReportComponent
      }
    },
    data: {
      noScrollTop: true,
      noMaxWidth: true,
      viewTitle: 'Report',
      translate: true,
      modules: [TranslationCategory.REPORTS, TranslationCategory.WEBSHOP ] // load reports translations, and webshop translations for lists/WEBSHOP
    },
    params: REPORT_PARAMS_DECLARATION,
    resolve: REPORT_PARAMS_RESOLVER
  },
  {
    name: STATE_BROWSELIST,
    url: `/lists/{listCode}?${REPORT_QUERY_PARAMS}&ga1AddUrl`,
    views: {
      content: {
        component: ReportComponent
      }
    },
    data: {
      noScrollTop: true,
      viewTitle: 'List',
      translate: true,
      modules: [ TranslationCategory.REPORTS, TranslationCategory.WEBSHOP ] // load reports translations, and webshop translations for lists/WEBSHOP
    },
    params: REPORT_PARAMS_DECLARATION,
    resolve: REPORT_PARAMS_RESOLVER
  },
  {
    name: STATE_DASHBOARD,
    url: '/dashboard/{dashboardCode}?filters&showfilters',
    views: {
      content: {
        component: DashboardComponent
      }
    },
    data: {
      noScrollTop: true,
      translate: {
        routeParams: {
          [DashboardCode.CUSTOMER]: { translate: true },
          [DashboardCode.COUNTRY_HIT_RATE]: { translate: true },
          [DashboardCode.WEBSHOP_HIT_RATE]: { translate: true },
          [DashboardCode.OD_ORDER]: { translate: true, languages: [Lang.DE] },
          [DashboardCode.OD_BP]: { translate: true, languages: [Lang.DE] },
          [DashboardCode.GBDM]: { translate: true, languages: [Lang.DE] },
          [DashboardCode.SALES_KPI]: { translate: true, languages: [Lang.DE] },
          [DashboardCode.SALES_DASHBOARD]: { translate: true, languages: [Lang.DE] },
          [DashboardCode.OEM_KPI]: { translate: true, languages: [Lang.DE] }
        }
      },
      modules: [TranslationCategory.DASHBOARD, TranslationCategory.WEBSHOP] // load webshop translations for GBDM_NEW dashboard browselist
    },
    params: {
      filters: { dynamic: true },
      showfilters: { dynamic: true }
    },
    resolve: [
      TRANSLATION_READY_RESOLVER,
      { token: 'dashboardCode', deps: [Transition], resolveFn: (transition:Transition) => transition.params().dashboardCode },
      { token: 'showFiltersParameter', deps: [Transition], resolveFn: (transition:Transition) => util.stringToBoolean(transition.params().showfilters) },
      {
        token: 'filters',
        deps: [Transition, ReportFiltersUtil],
        resolveFn: (transition:Transition, reportFiltersUtil:ReportFiltersUtil) => {
          const filters:IObject = reportFiltersUtil.paramToFilters(transition.params().filters);
          return util.convertPropValueToNumber(filters);
        }
      }
    ]
  },
  {
    name: STATE_DASHBOARDUSAGE,
    url: '/dashboard-usage',
    component: DashboardUsageComponent,
    data: {
      modules: [TranslationCategory.DASHBOARD] // load dashboard translation keys
    },
    resolve: [TRANSLATION_READY_RESOLVER]
  },
  {
    name: STATE_BROWSELISTUSAGE,
    url: '/browselist-usage',
    component: BrowselistUsageComponent
  },
  {
    name: STATE_DESK_OVERVIEW,
    url: '/desks?userId',
    params: {
      userId: {
        dynamic: true
      }
    },
    views: {
      content: {
        component: DeskOverviewComponent
      }
    },
    data: {
      noMaxWidth: true
    },
    resolve: [
      TRANSLATION_READY_RESOLVER,
      {
        token: 'userId',
        deps: [Transition],
        resolveFn: (transition:Transition) => transition.params().userId
      }
    ]
  },
  {
    name: STATE_CHART,
    url: `/chart?customer&from&to&currency&webshop&bp&bpGroup&testOrder`,
    params: {
      customer: { dynamic: true },
      from: { dynamic: true },
      to: { dynamic: true },
      currency: { dynamic: true },
      webshop: { dynamic: true },
      bp: { dynamic: true },
      bpGroup: { dynamic: true },
      testOrder: { dynamic: true }
    },
    views: {
      content: {
        component: ChartComponent
      }
    },
    data: {
      modules: [TranslationCategory.CHART]
    },
    resolve: [TRANSLATION_READY_RESOLVER]
  },
  {
    name: STATE_CHART_CUSTOMER,
    url: '/customer',
    component: ChartCustomerComponent
  },
  {
    name: STATE_CHART_BP,
    url: '/bp',
    component: ChartBpComponent
  }
];

@NgModule({
  imports: [
    UIRouterModule.forChild({ states }),
    CommonModule,
    SharedModule,
    BsDropdownModule
  ],
  declarations: [
    DashboardUsageComponent,
    BrowselistUsageComponent,
    ReportComponent,
    ReportFiltersComponent,
    ReportFiltersCollapsibleComponent,
    StoredReportComponent,
    StoredReportDialogComponent,
    StoredReportDialogNewButtonComponent,
    ReportTableSettingsComponent,
    ReportSQLComponent,
    DeskOverviewComponent,
    ChartComponent,
    ChartCustomerComponent,
    ChartBpComponent,
    FullChartDialogComponent,
    DashboardComponent,
    DashboardSectionComponent,
    DashboardSectionsComponent,
    // dashboard filters
    BaseDashboardFiltersComponent,
    TestDashboardFiltersComponent,
    OdDashboardFiltersComponent,
    CustomerDashboardFiltersComponent,
    SalesKpiDashboardFiltersComponent,
    LbdmDashboardFiltersComponent,
    ActivationDashboardFiltersComponent,
    OemDashboardFiltersComponent,
    BpSetupDashboardFiltersComponent,
    HitRateDashboardFiltersComponent,
    // dashboard info
    BpLocalEurInfoComponent,
    NumberOfFansInfoComponent,
    SalesDashboardInfoComponent,
    SalesKpiInfoComponent,
    LbdmRevenueUsdInfoComponent,
    ActivationKpiInfoComponent,
    // dashboard header
    LbdmDashboardHeaderComponent,
    OrderDashboardHeaderComponent,
    // dashboard footer
    SalesKpiFooterComponent,
    ActivationKpiFooterComponent,
    // dashboard subtitle
    CustomerDashboardSubtitleComponent
  ],
  providers: [
    DashboardMetadataService,
    DashboardService,
    DeskOverviewDataService,
    ChartService,
    OdDataService,
    CustomerDataService
  ],
  exports: [
    DashboardComponent
  ]
})
export class ReportModule { }

